// StayUpdated.jsx - The free price watch. One form, used in three places, and
// the page that the "Stay updated" link on every embed opens.
//
// This is the only thing we ask for in return for the charts and the embeds, so
// the shortest path through it is: arrive, type an email address, press the
// button. Everything else is already filled in. The metal comes from the link
// the widget was showing, the rule is pre-ticked at one percent in twenty four
// hours, and the price to watch for is pre-filled with a round number just above
// where the market is now, so that somebody who has never thought about a price
// target still has a sensible one.
//
// Nothing is sent until the address is confirmed by email. That is said on the
// button and again in the answer, because a person who does not see a message
// arrive assumes it is broken.
//
// LEGEND
//   1. roundTarget()   the pre-filled price, one decision the visitor is spared
//   2. AlertForm       the form itself, inline anywhere
//   3. PriceAlerts     the page the embeds link to

import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router";
import clsx from "clsx";

import { Seo, JsonLd, breadcrumbSchema } from "../lib/seo.jsx";
import { useSpot, useForm } from "../lib/hooks.js";
import { publicApi } from "../lib/api.js";
import * as analytics from "../lib/analytics.js";
import { CONSENT } from "../lib/legal.js";
import { METALS, metalOf, usd } from "../lib/chart.js";
import { Input, Checkbox, Button, Alert, Panel } from "../ui/kit.jsx";
import { Bell, Check, ArrowRight } from "../ui/icons.jsx";
import { Reveal } from "../ui/motion.jsx";
import { PageHead, CtaBand } from "./parts.jsx";

////////// 1. THE PRE-FILLED TARGET //////////
// A round number a person would have picked themselves, a little above where the
// market is. Gold rounds to fifty, silver to a dollar, and so on, which is what
// stops the box offering $4,487.31 as a price target.

function roundTarget(price, direction) {
  if (!price) return "";
  const step = price > 2000 ? 50 : price > 500 ? 25 : price > 100 ? 5 : price > 20 ? 1 : 0.5;
  const nudged = direction === "above" ? price * 1.02 : price * 0.98;
  const rounded = direction === "above" ? Math.ceil(nudged / step) * step : Math.floor(nudged / step) * step;
  return String(Number(rounded.toFixed(2)));
}

const HOURS = [
  { value: 1, label: "1 hour" },
  { value: 6, label: "6 hours" },
  { value: 24, label: "24 hours" },
  { value: 72, label: "3 days" },
  { value: 168, label: "a week" },
];

const PERCENTS = [0.5, 1, 2, 3, 5].map((value) => ({ value, label: `${value}%` }));

////////// 2. THE FORM //////////

export function AlertForm({ metal: initialMetal = "gold", source = "site", className, onDone }) {
  const { spot } = useSpot();
  const [metal, setMetal] = useState(initialMetal);
  const [open, setOpen] = useState(false);
  const [done, setDone] = useState(null);

  // The three rules. The first is on, because a form with nothing ticked needs
  // a decision before it needs an email address.
  const [move, setMove] = useState(true);
  const [percent, setPercent] = useState(1);
  const [withinHours, setWithinHours] = useState(24);
  const [level, setLevel] = useState(false);
  const [direction, setDirection] = useState("above");
  const [price, setPrice] = useState("");
  const [digest, setDigest] = useState(false);

  const form = useForm({ email: "", phone: "", smsConsent: false, website: "" });
  const current = spot?.perOunce?.[metal];

  // Refill the target whenever the thing it is a target for changes, unless the
  // person has already typed one of their own.
  const [touchedPrice, setTouchedPrice] = useState(false);
  useEffect(() => {
    if (touchedPrice) return;
    setPrice(roundTarget(current, direction));
  }, [current, direction, touchedPrice]);

  const rules = useMemo(() => [
    ...(move ? [{ kind: "move", metal, percent, withinHours }] : []),
    ...(level && price ? [{ kind: "level", metal, direction, price: Number(price) }] : []),
    ...(digest ? [{ kind: "digest" }] : []),
  ], [move, level, digest, metal, percent, withinHours, direction, price]);

  const submit = form.submit(async (values) => {
    if (!rules.length) {
      form.setMessage({ kind: "error", text: "Choose at least one thing to be told about." });
      return null;
    }

    const result = await publicApi.watchPrices({
      email: values.email,
      phone: values.smsConsent ? values.phone : undefined,
      smsConsent: Boolean(values.smsConsent && values.phone),
      rules,
      source,
      sourceHost: typeof window === "undefined" ? undefined : window.location.host,
      website: values.website,
    });

    setDone(result);
    // Secondary, and it stays secondary: a free price watch is worth reporting
    // and is not worth bidding on. See section 4 of docs/google-ads.md.
    analytics.trackPriceAlert({ rules: rules.length, source });
    onDone?.(result);
    return result;
  });

  if (done) {
    return (
      <Panel className={clsx("p-6 sm:p-7", className)}>
        <p className="flex items-center gap-2.5 display display-sm text-ink">
          <Check size={20} className="text-positive" />
          {done.confirmed ? "Added" : "Check your email"}
        </p>
        <p className="hint mt-3 leading-relaxed measure">{done.message}</p>
        {done.watching?.length > 0 && (
          <ul className="mt-4 pt-4 border-t border-rule-soft space-y-1.5">
            {done.watching.map((line) => (
              <li key={line} className="text-[14px] text-ink-soft flex gap-2.5">
                <Bell size={15} className="text-bronze shrink-0 mt-0.5" />
                <span className="first-letter:uppercase">{line}</span>
              </li>
            ))}
          </ul>
        )}
        <p className="fineprint mt-4 text-[12.5px] text-faint leading-relaxed">
          Every message has a one click way out at the bottom. There is no account and nothing to cancel.
        </p>
      </Panel>
    );
  }

  return (
    <Panel className={clsx("p-5 sm:p-6 md:p-7", className)}>
      <form onSubmit={submit} noValidate>
        <p className="eyebrow">Free price alerts</p>
        <h2 className="display display-md text-ink mt-2">Tell me when it moves.</h2>
        <p className="hint mt-2.5 leading-relaxed measure">
          No account, no charge. We watch the market and send one message when the thing you picked happens.
        </p>

        {/* Which metal. A row of four, because a dropdown for four options is a
            click somebody does not need to spend. */}
        <div className="seg mt-5" role="group" aria-label="Metal to watch">
          {METALS.map((option) => (
            <button
              key={option.key}
              type="button"
              className="seg-item"
              aria-pressed={metal === option.key}
              onClick={() => setMetal(option.key)}
            >
              {option.label}
            </button>
          ))}
        </div>

        <div className="mt-5 space-y-3.5">
          <Checkbox
            checked={move}
            onChange={(event) => setMove(event.target.checked)}
            label={
              <span className="inline-flex flex-wrap items-baseline gap-x-1.5 gap-y-2">
                Tell me when {metalOf(metal).label.toLowerCase()} moves more than
                <InlineSelect value={percent} onChange={setPercent} options={PERCENTS} numeric />
                within
                <InlineSelect value={withinHours} onChange={setWithinHours} options={HOURS} numeric />
              </span>
            }
          />

          {!open && (
            <button type="button" className="link text-[13.5px] text-muted hover:text-ink" onClick={() => setOpen(true)}>
              Add a price target or a weekly summary
            </button>
          )}

          {open && (
            <>
              <Checkbox
                checked={level}
                onChange={(event) => setLevel(event.target.checked)}
                label={
                  <span className="inline-flex flex-wrap items-baseline gap-x-1.5 gap-y-2">
                    Tell me when it goes
                    <InlineSelect
                      value={direction}
                      onChange={setDirection}
                      options={[{ value: "above", label: "above" }, { value: "below", label: "below" }]}
                    />
                    <input
                      type="text"
                      inputMode="decimal"
                      value={price}
                      onChange={(event) => { setTouchedPrice(true); setPrice(event.target.value.replace(/[^\d.]/g, "")); }}
                      onFocus={() => setLevel(true)}
                      aria-label="Price per troy ounce"
                      className="w-24 px-2 py-1 border border-rule bg-card text-ink num text-[13.5px] focus:border-bronze outline-none"
                    />
                    per troy ounce
                  </span>
                }
                hint={current ? `${metalOf(metal).label} is ${usd(current)} right now.` : undefined}
              />

              <Checkbox
                checked={digest}
                onChange={(event) => setDigest(event.target.checked)}
                label="Send me a short summary of all four metals once a week"
              />
            </>
          )}
        </div>

        <div className="mt-6 grid gap-3.5 sm:grid-cols-[1fr_auto] sm:items-end">
          <Input
            {...form.field("email")}
            type="email"
            label="Email"
            placeholder="you@example.com"
            autoComplete="email"
            required
          />
          <Button type="submit" loading={form.busy} className="w-full sm:w-auto sm:mb-[1px]">
            Watch the price <ArrowRight size={16} />
          </Button>
        </div>

        {/* The honeypot. Not visible, never focusable, never announced. */}
        <input
          {...form.field("website")}
          type="text"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          className="absolute w-px h-px -left-[9999px] opacity-0"
        />

        <div className="mt-4 pt-4 border-t border-rule-soft space-y-3">
          <Checkbox
            checked={form.values.smsConsent}
            onChange={(event) => form.set("smsConsent", event.target.checked)}
            label="Text me as well as emailing me"
            hint="Optional. Email works on its own."
          />
          {form.values.smsConsent && (
            <>
              <Input
                {...form.field("phone")}
                type="tel"
                label="Mobile number"
                placeholder="(555) 555-5555"
                autoComplete="tel"
              />
              {/* Word for word what the server stamps onto the stored consent
                  record. If one changes, both change. */}
              <p className="fineprint text-[11.5px] leading-relaxed text-faint">
                {CONSENT.alertSms}{" "}
                <Link to="/legal/sms" className="underline">Text message terms</Link>
                {" and "}
                <Link to="/legal/privacy" className="underline">privacy policy</Link>.
              </p>
            </>
          )}
        </div>

        {form.message && (
          <Alert tone={form.message.kind === "error" ? "error" : "info"} className="mt-4">
            {form.message.text}
          </Alert>
        )}

        <p className="fineprint mt-4 text-[12.5px] text-faint leading-relaxed">
          We send the alert you asked for and nothing else. Confirm by email first, and every message
          carries a one click way out. We do not sell or share the address.
        </p>
      </form>
    </Panel>
  );
}

// A select that sits inside a sentence rather than above a label. Clicking it
// must not toggle the tick box it is written into, hence the stopped events.
function InlineSelect({ value, onChange, options, numeric = false }) {
  return (
    <select
      value={value}
      onClick={(event) => event.stopPropagation()}
      onChange={(event) => {
        event.stopPropagation();
        onChange(numeric ? Number(event.target.value) : event.target.value);
      }}
      className="px-1.5 py-0.5 border border-rule bg-card text-ink text-[13.5px] num cursor-pointer focus:border-bronze outline-none"
    >
      {options.map((option) => (
        <option key={option.value} value={option.value}>{option.label}</option>
      ))}
    </select>
  );
}

////////// 3. THE PAGE //////////
// Where every "Stay updated" link on every embed lands. It carries the metal the
// widget was showing, so the form opens already pointed at what the reader was
// looking at when they clicked.

export default function PriceAlerts() {
  const [params] = useSearchParams();
  const metal = METALS.some((option) => option.key === params.get("metal")) ? params.get("metal") : "gold";
  const fromEmbed = params.get("from") === "embed";

  return (
    <>
      <Seo
        title="Free gold and silver price alerts"
        path="/price-alerts"
        description="Get an email or a text when gold, silver, platinum or palladium moves sharply or crosses a price you pick. Free, no account, one click to stop."
      />
      <JsonLd data={breadcrumbSchema([{ label: "Home", to: "/" }, { label: "Price alerts", to: "/price-alerts" }])} />

      <PageHead
        eyebrow={fromEmbed ? "From the chart you were reading" : "Free, no account"}
        title="Know the moment it moves."
        lede="Pick a metal and what would be worth interrupting you for. We watch the market and send one message when it happens, by email, by text, or both."
      />

      <div className="shell pb-14 md:pb-20 grid gap-10 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)] lg:gap-14 items-start">
        <AlertForm metal={metal} source={fromEmbed ? "embed" : "site"} />

        <Reveal className="lg:pt-6">
          <h2 className="display display-md text-ink">What you actually get</h2>
          <dl className="mt-5 space-y-5">
            {[
              ["One message, not a newsletter", "We send the thing you asked for. There is no daily email and no sequence underneath it."],
              ["A hard limit", "At most three messages in a day, whatever the market does. A volatile afternoon does not become eleven texts."],
              ["Spot prices, not our prices", "Every number in an alert is the open market price of pure metal, which you can check against any public feed."],
              ["One click out", "Every message has a link at the bottom that stops them. There is no account to close and nothing to log in to."],
            ].map(([term, detail]) => (
              <div key={term}>
                <dt className="font-semibold text-[15px] text-ink">{term}</dt>
                <dd className="hint mt-1.5 leading-relaxed measure">{detail}</dd>
              </div>
            ))}
          </dl>

          <p className="hint mt-7 pt-6 border-t border-rule-soft leading-relaxed measure">
            Want the chart itself on your own site? The{" "}
            <Link to="/embed" className="underline decoration-bronze-soft hover:decoration-bronze">embed builder</Link>{" "}
            gives you a line to paste. It is free, it needs no key, and you may leave it up for as long as you like.
          </p>
        </Reveal>
      </div>

      <CtaBand
        title="Watching the price is the easy half."
        body="Knowing what your own pieces come to means weighing and testing them. That part is free too, and you see every number before you decide anything."
      />
    </>
  );
}
